import { useState } from "react";
import {
  Stack,
  Tabs,
  Button,
  Group,
  Box,
  Flex,
} from "@mantine/core";
import {
  FileType,
  Mail as MailIcon,
  Building2,

  Save,
  RotateCcw,
  AlertCircle,
  Link2,
  Scan,
} from "lucide-react";
import { showSuccess, showError } from "../utils/notifications";
import { useMediaQuery } from "@mantine/hooks";
import {
  Partner,
  updatePartner,
  deletePartner,
  checkPartnerStatus,
} from "../services/partnerService";
import { CsvConfigurationPanel } from "./CsvConfigurationPanel";
import { EmailConfigurationPanel } from "./EmailConfigurationPanel";
import { EmailConfig, SlipTypeConfig } from "../services/csvSettingsService";
import { TestPlaygroundTab } from "./TestPlaygroundTab";
import { BasicInfoPanel } from "./partner/BasicInfoPanel";
import { PatternSettingsPanel } from "./partner/PatternSettingsPanel";

interface PartnerSettingsTabsProps {
  customerId: string;
  partner: Partner;
  onUpdate: (silent?: boolean) => void;
}

export function PartnerSettingsTabs({
  customerId,
  partner,
  onUpdate,
}: PartnerSettingsTabsProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [activeTab, setActiveTab] = useState<string | null>("basic");
  const [saving, setSaving] = useState(false);

  // 編集用ステート（初期値はPropsから）
  const [officialName, setOfficialName] = useState(partner.officialName);
  const [emailConfig, setEmailConfig] = useState<EmailConfig>(
    structuredClone(partner.emailConfig || { replyTo: [] })
  );
  const [slipTypeConfigs, setSlipTypeConfigs] = useState<
    Record<string, SlipTypeConfig>
  >(structuredClone(partner.slipTypeConfigs || {}));

  // 変更があるかチェック（簡易的）
  const hasChanges =
    officialName !== partner.officialName ||
    JSON.stringify(emailConfig) !==
      JSON.stringify(partner.emailConfig || { replyTo: [] }) ||
    JSON.stringify(slipTypeConfigs) !==
      JSON.stringify(partner.slipTypeConfigs || {});

  // リセット
  const handleReset = () => {
    if (!confirm("変更を破棄して元に戻しますか？")) return;
    setOfficialName(partner.officialName);
    setEmailConfig(structuredClone(partner.emailConfig || { replyTo: [] }));
    setSlipTypeConfigs(structuredClone(partner.slipTypeConfigs || {}));
  };

  // 保存処理
  const handleSave = async () => {
    if (!officialName.trim()) return;
    setSaving(true);
    try {
      await updatePartner(customerId, partner.id, {
        officialName,
        emailConfig,
        slipTypeConfigs,
      });
      showSuccess("取引先設定を更新しました", "保存しました");
      onUpdate(true);
    } catch {
      showError("保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  // 削除処理
  const handleDelete = async () => {
    if (
      !confirm(
        `「${partner.officialName}」を削除しますか？\n紐付いているパターンは未分類に戻ります。`
      )
    ) {
      return;
    }
    try {
      await deletePartner(customerId, partner.id);
      showSuccess("取引先を削除しました", "削除しました");
      onUpdate(); // 親側でリスト更新されることを期待
    } catch {
      showError("削除に失敗しました");
    }
  };

  // タブスタイル（アクティブ時）
  const getActiveTabStyle = (tabValue: string) =>
    activeTab === tabValue
      ? {
          color:
            "light-dark(var(--mantine-color-blue-7), var(--mantine-color-blue-4))",
          borderColor:
            "light-dark(var(--mantine-color-blue-7), var(--mantine-color-blue-4))",
          borderBottomColor:
            "light-dark(var(--mantine-color-white), var(--mantine-color-dark-6))",
        }
      : {};

  const { isCsvConfigured, isEmailConfigured } = checkPartnerStatus(partner);

  return (
    <Stack gap="md" pt="xs">
      {/* タブナビゲーション */}
      <Tabs
        value={activeTab}
        onChange={setActiveTab}
        variant="outline"
        color="blue"
        radius="xs"
        mb="md"
      >
        {/* タブリストと保存ボタンを同じ行に配置 */}
        <Flex justify="space-between" align="center" wrap="wrap" gap="sm">
          <Tabs.List>
            <Tabs.Tab
              value="basic"
              leftSection={<Building2 size={16} />}
              style={getActiveTabStyle("basic")}
            >
              基本情報
            </Tabs.Tab>
            <Tabs.Tab
              value="csv"
              leftSection={<FileType size={16} />}
              rightSection={
                !isCsvConfigured && (
                  <AlertCircle size={14} color="var(--mantine-color-red-6)" />
                )
              }
              style={getActiveTabStyle("csv")}
            >
              CSV設定
            </Tabs.Tab>
            <Tabs.Tab
              value="email"
              leftSection={<MailIcon size={16} />}
              rightSection={
                !isEmailConfigured && (
                  <AlertCircle size={14} color="var(--mantine-color-red-6)" />
                )
              }
              style={getActiveTabStyle("email")}
            >
              メール設定
            </Tabs.Tab>
            <Tabs.Tab
              value="patterns"
              leftSection={<Link2 size={16} />}
              style={getActiveTabStyle("patterns")}
            >
              名寄せ設定
            </Tabs.Tab>
            <Tabs.Tab
              value="playground"
              leftSection={<Scan size={16} />}
              style={getActiveTabStyle("playground")}
            >
              検証
            </Tabs.Tab>
          </Tabs.List>

          {/* 保存ボタン群 */}
          <Group gap="xs">
            {hasChanges && (
              <Button
                variant="default"
                size="sm"
                leftSection={<RotateCcw size={14} />}
                onClick={handleReset}
              >
                元に戻す
              </Button>
            )}
            <Button
              leftSection={<Save size={16} />}
              loading={saving}
              onClick={handleSave}
              disabled={!hasChanges}
            >
              変更を保存
            </Button>
          </Group>
        </Flex>

        <Box p={isMobile ? "xs" : "md"}>
          <Tabs.Panel value="basic">
            <BasicInfoPanel
              partner={partner}
              officialName={officialName}
              onOfficialNameChange={setOfficialName}
              onDelete={handleDelete}
            />
          </Tabs.Panel>

          <Tabs.Panel value="csv">
            <CsvConfigurationPanel
              value={{ columns: [], encoding: "utf-8" }}
              onChange={() => {}}
              slipTypeConfigs={slipTypeConfigs}
              onChangeSlipTypeConfigs={setSlipTypeConfigs}
            />
          </Tabs.Panel>

          <Tabs.Panel value="email">
            <EmailConfigurationPanel
              value={emailConfig}
              onChange={setEmailConfig}
            />
          </Tabs.Panel>

          <Tabs.Panel value="patterns">
            <PatternSettingsPanel
              customerId={customerId}
              partner={partner}
              officialName={officialName}
              onNavigateToBasic={() => setActiveTab("basic")}
              onUpdate={onUpdate}
            />
          </Tabs.Panel>

          {/* 検証タブ */}
          <Tabs.Panel value="playground">
            <TestPlaygroundTab
              customerId={customerId}
              partnerId={partner.id}
              partnerName={partner.officialName}
            />
          </Tabs.Panel>
        </Box>
      </Tabs>
    </Stack>
  );
}
